/**
 * Maps funnel values (ratio-based, centered around 100) onto a 0-100 scale using a linear curve.
 * 0 → 0, 100 → 50 (center), 200 → 100.
 */
export const exponentialPosition = (x: number): number => {
  const result = x / 2;
  return Math.max(0, Math.min(100, result));
};

/**
 * Maps a percentile (0-100) to a dial position (0-100) using a linear scale.
 * 0 → left, 50 → top center, 100 → right.
 *
 * @param percentile - Percentile rank from 0 to 100
 * @returns Dial position between 0 and 100
 */
export const percentileToDialPosition = (percentile: number): number => {
  return Math.max(0, Math.min(100, percentile));
};

/**
 * Get color based on percentage value (0-100)
 * Scales from deep red (worst) to very green (best)
 * Green starts appearing around 50%
 * More sensitive around 50% (which represents benchmark value of 100)
 * Uses finer color segments around 50% to make small differences more visible
 * 
 * @param percent - Percentage value from 0 to 100
 * @returns Hex color string
 */
export const getScaledColor = (percent: number): string => {
  const clampedPercent = Math.max(0, Math.min(100, percent));
  
  // Use piecewise linear segments with finer gradients around 50%
  // This creates more color variation for values near 100 (which maps to 50%)
  
  if (clampedPercent <= 40) {
    // 0-40%: Deep red to orange-red
    const ratio = clampedPercent / 40;
    // Deep red (#991B1B) to orange-red (#DC2626)
    const r = Math.round(153 + (220 - 153) * ratio);
    const g = Math.round(27 + (38 - 27) * ratio);
    const b = Math.round(27 + (38 - 27) * ratio);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  } else if (clampedPercent <= 50) {
    // 40-50%: Orange-red to yellow-green (finer gradient for sensitivity)
    const ratio = (clampedPercent - 40) / 10;
    // Orange-red (#DC2626) to yellow-green (#84CC16)
    const r = Math.round(220 + (132 - 220) * ratio);
    const g = Math.round(38 + (204 - 38) * ratio);
    const b = Math.round(38 + (22 - 38) * ratio);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  } else if (clampedPercent <= 60) {
    // 50-60%: Yellow-green to green (finer gradient for sensitivity)
    const ratio = (clampedPercent - 50) / 10;
    // Yellow-green (#84CC16) to green (#10B981)
    const r = Math.round(132 + (16 - 132) * ratio);
    const g = Math.round(204 + (185 - 204) * ratio);
    const b = Math.round(22 + (129 - 22) * ratio);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  } else {
    // 60-100%: Green to very green
    const ratio = (clampedPercent - 60) / 40;
    // Green (#10B981) to very green (#047857)
    const r = Math.round(16 + (4 - 16) * ratio);
    const g = Math.round(185 + (120 - 185) * ratio);
    const b = Math.round(129 + (87 - 129) * ratio);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }
};
